import { useId, useState } from 'react';

import { Route42InlineMarkdown, Route42Markdown } from '../../../components/ui/Route42Markdown';
import { ROUTE42_ASSISTANT_NAME } from '../../../config/identity';
import type { ConversationTurnDto } from '../../conversations/types/conversation';
import { deriveEvidenceStrength } from '../../conversations/types/conversation';
import { formatThreadTimestamp, formatThreadTimestampTitle } from '../../conversations/utils/thread-time';
import type { InvestigationRoleDto } from '../types/investigation';

export function InvestigationThreadEntry({
  turn,
  currentUserId,
  actorName,
  role,
  onEvidence,
  compact = false,
}: {
  turn: ConversationTurnDto;
  currentUserId?: string | null;
  actorName?: string | null;
  role?: InvestigationRoleDto | null;
  onEvidence: () => void;
  compact?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const messageId = useId();
  const longMessage = compact && turn.question.length > 300;
  const collapsed = longMessage && !expanded;
  const isSelf = Boolean(currentUserId && turn.user_id === currentUserId);
  const actor = isSelf ? 'You' : humanActor(actorName || turn.actor_label);
  const askedAce = turn.interaction_type !== 'TEAM_UPDATE' || Boolean(turn.answer);
  const answer = turn.answer;

  return (
    <article className="r42-investigation-thread-entry" data-testid="investigation-thread-entry">
      <div className="r42-investigation-team-entry">
        <div className="r42-investigation-team-avatar" aria-hidden="true">{initials(actor)}</div>
        <div className="r42-investigation-team-entry__body">
          <div className="r42-investigation-team-entry__meta">
            <strong>{actor}</strong>
            <span>{humanRole(role)}</span>
            {turn.created_at ? <time dateTime={turn.created_at} title={formatThreadTimestampTitle(turn.created_at)}>{formatThreadTimestamp(turn.created_at)}</time> : null}
            {askedAce ? <span className="r42-investigation-asked-ace">Asked {ROUTE42_ASSISTANT_NAME}</span> : null}
          </div>
          <div id={messageId} className={collapsed ? 'r42-investigation-message--collapsed' : undefined}>
            <Route42Markdown className="r42-investigation-team-entry__text" content={turn.question} />
          </div>
          {longMessage ? <button type="button" className="r42-investigation-message-expand" aria-controls={messageId} aria-expanded={expanded} onClick={() => setExpanded((value) => !value)}>{expanded ? 'Show less' : askedAce ? 'Read full question' : 'Read full update'}</button> : null}
        </div>
      </div>

      {answer ? (
        <div className="r42-investigation-ace-entry">
          <div className="r42-investigation-ace-entry__meta">
            <span className="r42-investigation-ace-avatar"><i className="bi bi-compass" aria-hidden="true" /></span>
            <strong>{ROUTE42_ASSISTANT_NAME}</strong>
            <span>Shared reasoning</span>
          </div>
          <Route42Markdown className="r42-answer-text" content={answer.answer_text} />
          {isIndustrialGuidance(answer.knowledge_basis) ? (
            <div className="r42-answer-basis" aria-label="Answer knowledge basis">
              <span>{answer.knowledge_basis === 'CONTEXTUAL_INDUSTRIAL_GUIDANCE' ? 'Industrial guidance · current work context' : 'General industrial guidance'}</span>
              <p>{answer.knowledge_basis === 'CONTEXTUAL_INDUSTRIAL_GUIDANCE' ? 'Ace can use the work context you are in, but this is not a conclusion about the machine.' : 'This answer uses general industrial knowledge, not evidence from this machine.'}</p>
            </div>
          ) : answer.knowledge_basis === 'OUT_OF_SCOPE' ? null : (
            <div className="r42-answer-meta">
              <span className={`r42-answer-strength r42-answer-strength--${deriveEvidenceStrength(answer).toLocaleLowerCase()}`}>{humanize(deriveEvidenceStrength(answer))} evidence</span>
              {answer.conflicting_evidence ? <span className="r42-answer-conflict">Conflicting evidence</span> : null}
              {(answer.citations?.length ?? 0) > 0 ? <button onClick={onEvidence}>View evidence <span>{answer.citations.length}</span></button> : null}
            </div>
          )}
          {answer.uncertainty ? <p className="r42-answer-uncertainty"><strong>Uncertainty:</strong> <Route42InlineMarkdown content={answer.uncertainty} /></p> : null}
        </div>
      ) : null}
    </article>
  );
}

function humanActor(value: string | null | undefined): string {
  const text = String(value ?? '').trim();
  if (!text) return 'Team member';
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(text)) return 'Team member';
  if (/^(?:user|r42)_[0-9a-z_-]{8,}$/i.test(text)) return 'Team member';
  if (text.includes('@')) return text.split('@')[0].replace(/[._-]+/g, ' ').replace(/\b\w/g, (char) => char.toLocaleUpperCase());
  return text;
}

function initials(value: string): string {
  const parts = value.split(/\s+/).filter(Boolean);
  if (!parts.length) return 'TM';
  return parts.slice(0, 2).map((part) => part[0]?.toLocaleUpperCase() ?? '').join('');
}

function humanRole(role: InvestigationRoleDto | null | undefined): string {
  if (role === 'OWNER') return 'Investigation owner';
  if (role === 'EDITOR') return 'Investigation lead';
  if (role === 'VIEWER') return 'Viewer';
  return 'Contributor';
}

function isIndustrialGuidance(value: string | null | undefined): boolean {
  return value === 'GENERAL_INDUSTRIAL_KNOWLEDGE' || value === 'CONTEXTUAL_INDUSTRIAL_GUIDANCE';
}

function humanize(value: string): string {
  return value.toLocaleLowerCase().replace(/^./, (char) => char.toLocaleUpperCase());
}
